import React from 'react';

import { Icons } from '@shared/components/ui';

/**
 * The areas of life Conway covers, each with what its AI actually does there.
 *
 * Deliberately framed as *areas*, not as the four installable apps — the apps
 * are a consequence of an area being important enough to want on a home screen,
 * not the organizing idea. Framing by app would cap the story at four.
 */
const AREAS = [
  {
    icon: <Icons.Mail size="lg" />,
    name: 'Mail',
    hasApp: true,
    description:
      'Scores every message for urgency, labels it, and writes you a brief. You work an inbox that has already been triaged.',
  },
  {
    icon: <Icons.ChartPie size="lg" />,
    name: 'Money',
    hasApp: false,
    description:
      'Categorizes transactions as they land and keeps budget, cash flow, and retirement current.',
  },
  {
    icon: <Icons.Utensils size="lg" />,
    name: 'Food',
    hasApp: true,
    description:
      'Type "two eggs and toast." It resolves the foods, works out the macros, and logs it. No weighing, no barcode hunting.',
  },
  {
    icon: <Icons.ClipboardCheck size="lg" />,
    name: 'Tasks',
    hasApp: true,
    description:
      'Lists, due dates, and nesting — plus the tasks Conway opens on your behalf when something in another area needs doing.',
  },
  {
    icon: <Icons.Heart size="lg" />,
    name: 'Health',
    hasApp: false,
    description:
      'Metrics, medications, workouts, and personal records — with AI watching for the weeks you are quietly running yourself down.',
  },
  {
    icon: <Icons.Brain size="lg" />,
    name: 'Knowledge',
    hasApp: false,
    description:
      'Notes, journal, and highlights with search that works on meaning. Ask what you thought about something two years ago.',
  },
];

const AreasSection: React.FC = () => {
  return (
    <section id="areas" className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center sm:mb-16">
          <h2 className="text-foreground text-3xl font-light sm:text-4xl">
            An AI for Each <strong className="font-semibold">Area of Your Life</strong>
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
            Conway is organized into areas, and each one gets AI that understands that area
            specifically — not one chatbot bolted onto six different lists.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {AREAS.map(area => (
            <div key={area.name} className="bg-card border-border rounded-sm border p-6">
              <div className="text-muted-foreground bg-muted mb-4 flex h-10 w-10 items-center justify-center rounded-sm">
                {area.icon}
              </div>
              <div className="mb-2 flex items-baseline gap-2">
                <h3 className="text-foreground text-lg font-medium">{area.name}</h3>
                {area.hasApp && (
                  <span className="text-muted-foreground border-border rounded-sm border px-1.5 py-0.5 text-xs">
                    own app
                  </span>
                )}
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">{area.description}</p>
            </div>
          ))}
        </div>
        <p className="text-muted-foreground mt-8 text-center text-sm">
          Mail, Tasks, Food, and Chat each install as their own home-screen app, so the area you
          live in is one tap away.
        </p>
      </div>
    </section>
  );
};

export default AreasSection;
